/**
 * Tiny DOM helpers. Text always goes in as text nodes (never innerHTML), except the HTML fields the API
 * documents as HTML (Beds24 offer descriptions, WordPress room descriptions) — see html().
 */

/**
 * Every element the app creates carries TranslatePress's data-no-dynamic-translation (no-dynamic-translation
 * change, 19 Oct 2026): our texts are translated before they reach the browser (01 §13), so TP's lookup of
 * inserted text (trp-translate-dom-changes.js) must skip them. The block root carries it too, but TP tests it with
 * closest() when its observer runs: a node that a render inserts and removes again in the same task is detached by
 * then and only its own attribute (or a detached ancestor's) still covers it.
 * @param {string} tag
 * @returns {HTMLElement}
 */
function element(tag) {
	const el = document.createElement(tag);
	el.setAttribute('data-no-dynamic-translation', '');
	return el;
}

/**
 * Creates an element.
 * @param {string} tag
 * @param {Object} [attrs] class, text, html, on: {event: fn}, data: {key: value}, any other attribute.
 * @param {...(Node|string|null|false)} children
 * @returns {HTMLElement}
 */
export function h(tag, attrs = {}, ...children) {
	const el = element(tag);
	for (const [key, value] of Object.entries(attrs || {})) {
		if (value === null || value === undefined || value === false) {
			continue;
		}
		if (key === 'class') {
			el.className = value;
		} else if (key === 'text') {
			el.textContent = value;
		} else if (key === 'on') {
			for (const [event, fn] of Object.entries(value)) {
				el.addEventListener(event, fn);
			}
		} else if (key === 'data') {
			for (const [k, v] of Object.entries(value)) {
				el.dataset[k] = v;
			}
		} else if (value === true) {
			el.setAttribute(key, '');
		} else {
			el.setAttribute(key, String(value));
		}
	}
	for (const child of children.flat()) {
		if (child === null || child === undefined || child === false) {
			continue;
		}
		el.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
	}
	return el;
}

/**
 * Element with trusted HTML from the API's *Html fields (WordPress / Beds24 content, French, verbatim).
 * @param {string} tag
 * @param {string} cls
 * @param {string} markup
 * @returns {HTMLElement}
 */
export function html(tag, cls, markup) {
	const el = element(tag);
	el.className = cls;
	el.innerHTML = markup || '';
	return el;
}

/**
 * Removes all children. They go into a detached element marked data-no-dynamic-translation rather than nowhere:
 * a text node inserted and cleared again in the same task (a re-render) would otherwise reach TranslatePress's
 * observer with no parent, and TP sends every unparented text to its lookup (see element()).
 */
export function clear(node) {
	const bin = element('div');
	while (node.firstChild) {
		bin.appendChild(node.firstChild);
	}
	return node;
}
